import type React from 'react';
import { useEffect } from 'react';

import { AdornedInput, FormField, FormHelp, InlineFeedback, Label, Paragraph } from '@zillow/constellation-10';

import type { Section } from '../../..//models';
import { TENANT_DEFAULT } from '../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../utils/validatorTypes/PositiveIntegerValidator';
import InlineTooltip from '../../../components/Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function TenantDefault_v2({ section, setSectionValues }: Props) {
  function valueIsValid(value: string): boolean {
    return Number(value) >= TENANT_DEFAULT.BOUNDS.MIN_DAYS;
  }

  useEffect(() => {
    section.setCustomFieldValidator(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID, valueIsValid);
    section.setCustomFieldValidator(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE, valueIsValid);
  }, []);

  function onDaysAfterUnpaidChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID]: value,
    });
  }

  function onDaysAfterReceivingNoticeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE]: value,
    });
  }

  const daysAfterUnpaidError = section.getField(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID).hasError;
  const daysAfterReceivingNoticeError = section.getField(
    TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE,
  ).hasError;

  return (
    <>
      <Paragraph textStyle="body-lg-bold">
        How long is the grace period before the tenant{' '}
        <InlineTooltip
          tooltipBodyText="For a tenant to default on the lease, they must fail to live up to the obligations they agreed to in the
          lease."
        >
          defaults
        </InlineTooltip>{' '}
        on the lease?
      </Paragraph>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        error={daysAfterUnpaidError}
        label={
          <Label id="days-after-unpaid">
            Number of days after unpaid rent or failure to live up to other obligations
          </Label>
        }
        control={
          <AdornedInput.Root error={daysAfterUnpaidError}>
            <AdornedInput.Input
              data-test="days-after-unpaid"
              aria-labelledby="days-after-unpaid"
              type="number"
              onChange={onDaysAfterUnpaidChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_UNPAID)}
            />
            <AdornedInput.Adornment>days</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
        description={
          <>
            <FormHelp>{TENANT_DEFAULT.BOUNDS.MIN_DAYS} days is the minimum allowed and most common</FormHelp>
            {daysAfterUnpaidError && <FormHelp>Enter a number higher than {TENANT_DEFAULT.BOUNDS.MIN_DAYS}</FormHelp>}
          </>
        }
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        error={daysAfterReceivingNoticeError}
        label={<Label id="days-after-receiving-notice">Number of days after receiving notice from landlord</Label>}
        control={
          <AdornedInput.Root error={daysAfterReceivingNoticeError}>
            <AdornedInput.Input
              data-test="days-after-receiving-notice"
              aria-labelledby="days-after-receiving-notice"
              type="number"
              onChange={onDaysAfterReceivingNoticeChange}
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(TENANT_DEFAULT.FIELD_NAME.DAYS_AFTER_RECEIVING_NOTICE)}
            />
            <AdornedInput.Adornment>days</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
        description={
          <>
            <FormHelp>{TENANT_DEFAULT.BOUNDS.MIN_DAYS} days is the minimum allowed and most common</FormHelp>
            {daysAfterReceivingNoticeError && (
              <FormHelp>Enter a number higher than {TENANT_DEFAULT.BOUNDS.MIN_DAYS}</FormHelp>
            )}
          </>
        }
      />
      <InlineFeedback>
        Under the lease terms, if a tenant is in default, you must notify them before exercising any other rights under
        the lease.
      </InlineFeedback>
    </>
  );
}

TenantDefault_v2.description = 'Asks between days since failure and since receiving notice';

export default TenantDefault_v2;
